import { useEffect, useMemo, useRef, useState } from "react";
import type { GenerationErrorCode, MeResponse, MusicTrack } from "@studio/shared";
import { TitleEditor } from "../components/TitleEditor";
import { TrackCard } from "../components/TrackCard";
import { SongCover } from "../components/SongCover";
import { ErrorPage, InlineError, LoadingPage, StudioShell } from "../components/StudioShell";
import { ApiError, apiRequest, renameTrack, useJobProgress, useWork } from "../lib/api";
import { navigate } from "../lib/navigation";
import { loadSession, pathForStep, updateSession } from "../lib/session";
import { useSongCodes } from "../lib/songCodes";

const failureMessages: Record<GenerationErrorCode, string> = {
  // 🩸 종전 문구는 «가사가 길다면 줄여서» 였다 — 학생에게 **틀린 것을 가르쳤다.** 실측
  //    176건에서 가사 길이는 소요시간 변동의 15%밖에 설명하지 못하고(r=0.39), 상한을
  //    180초로 올린 뒤로는 400자(가사 상한) 예상 92초라 길이가 원인이 되는 구간이 아니다.
  //    2026-09-07 세션 367273 에서 266자 가사가 3연속 실패한 뒤 **그대로** 다시 눌러
  //    78·83초에 성공했다 — 학생이 해야 할 행동은 «줄이기» 가 아니라 «그대로 재시도» 다.
  // 🔑 «횟수는 줄지 않아요» 는 사실이다 — 한도는 성공한 트랙 수로 센다(003 마이그레이션).
  PROVIDER_TIMEOUT: "가사 때문이 아니에요. 잠시 뒤 그대로 다시 시도해 주세요 — 만든 횟수는 줄지 않아요",
  PROVIDER_REJECTED: "지금은 만들 수 없어요. 다시 시도해 주세요",
  INTERNAL: "지금은 만들 수 없어요. 다시 시도해 주세요",
  MODERATION_BLOCKED: "가사를 고쳐서 다시 시도해 주세요",
  BUDGET_EXHAUSTED: "오늘 만들 수 있는 양을 다 썼어요",
};

async function saveStep(workId: string, step: "style" | "done" | "humming"): Promise<void> {
  await apiRequest(`/api/works/${encodeURIComponent(workId)}/autosave`, {
    method: "POST",
    body: JSON.stringify({ step }),
  });
}

export function ResultPage({ workId }: { workId: string }) {
  const session = loadSession();
  const jobId = session?.activeJobId ?? null;
  const query = useWork(workId);
  const polling = useJobProgress(jobId);
  const [cancelRequested, setCancelRequested] = useState(session?.cancelRequested ?? false);
  const [cancelling, setCancelling] = useState(false);
  const [finishingTrack, setFinishingTrack] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [sessionEnded, setSessionEnded] = useState(false);
  const handledCancel = useRef(false);

  useEffect(() => {
    if (polling.progress?.state === "succeeded") void query.refresh();
  }, [polling.progress?.state, query.refresh]);

  useEffect(() => {
    if (polling.progress?.state !== "cancelled" || handledCancel.current) return;
    handledCancel.current = true;
    void saveStep(workId, "style")
      .catch(() => undefined)
      .finally(() => {
        updateSession({ activeJobId: null, cancelRequested: false });
        navigate(pathForStep(workId, "style"), { replace: true });
      });
  }, [polling.progress?.state, workId]);

  useEffect(() => {
    const updateSessionState = (event: Event): void => {
      const me = (event as CustomEvent<MeResponse>).detail;
      setSessionEnded(me.session_status === "ended");
    };
    window.addEventListener("mvs:me-state", updateSessionState);
    return () => window.removeEventListener("mvs:me-state", updateSessionState);
  }, []);

  const stop = async (): Promise<void> => {
    if (!jobId) return;
    setCancelling(true);
    setError(null);
    try {
      const result = await apiRequest<{ state: string }>(
        `/api/jobs/${encodeURIComponent(jobId)}/cancel`,
        { method: "POST", body: JSON.stringify({}) },
      );
      if (result.state === "cancelled") {
        await saveStep(workId, "style");
        updateSession({ activeJobId: null, cancelRequested: false });
        navigate(pathForStep(workId, "style"), { replace: true });
        return;
      }
      setCancelRequested(true);
      updateSession({ cancelRequested: true });
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : "중지 요청을 보내지 못했습니다.");
    } finally {
      setCancelling(false);
    }
  };

  /** 노래 제목 고치기 — 저장한 뒤 작품을 다시 읽어 카드 제목을 맞춘다. */
  const saveTrackTitle = async (trackId: string, title: string): Promise<void> => {
    try {
      await renameTrack(workId, trackId, title);
    } catch (caught) {
      throw new Error(caught instanceof ApiError ? caught.message : "제목을 저장하지 못했어요.");
    }
    await query.refresh();
  };

  const finish = async (track: MusicTrack): Promise<void> => {
    setFinishingTrack(track.id);
    setError(null);
    try {
      await apiRequest(`/api/works/${encodeURIComponent(workId)}/finish`, {
        method: "POST",
        // 완성 화면에서 「이전」으로 돌아와 다른 곡을 고를 수 있다 — 갤러리에 올려 둔 것은 그대로 둔다.
        body: JSON.stringify({ track_id: track.id, gallery_consent: query.data?.work.gallery_consent ?? false }),
      });
      updateSession({ activeJobId: null, cancelRequested: false });
      navigate(pathForStep(workId, "done"));
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : "노래를 선택하지 못했습니다.");
    } finally {
      setFinishingTrack(null);
    }
  };

  /**
   * 🩸 2026-10-08: 허밍 곡이 제공자에게 거절되면 「다시 시도」 가 **같은 녹음**으로 다시 만들어
   *    같은 이유로 4번 연속 거절됐다. 허밍 거절은 녹음을 바꿔야 풀리므로 허밍 화면으로 보낸다.
   */
  const recordAgain = async (): Promise<void> => {
    try {
      await saveStep(workId, "humming");
      updateSession({ activeJobId: null, cancelRequested: false });
      navigate(pathForStep(workId, "humming"));
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : "허밍 화면으로 돌아가지 못했습니다.");
    }
  };

  const editAgain = async (): Promise<void> => {
    try {
      await saveStep(workId, "style");
      updateSession({ activeJobId: null, cancelRequested: false });
      navigate(pathForStep(workId, "style"));
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : "이전 단계로 돌아가지 못했습니다.");
    }
  };

  const state = polling.progress?.state;
  const generating = Boolean(jobId && (!state || ["queued", "dispatched", "cancel_requested"].includes(state)));
  const failed = state === "failed" || state === "outcome_unknown";
  const failureCode = polling.progress?.error_code ?? (state === "outcome_unknown" ? "INTERNAL" : undefined);
  const hummingRejected = failed && failureCode === "PROVIDER_REJECTED" && query.data?.work.mode === "humming";
  const tracks = query.data?.tracks ?? [];
  // 🚨 마지막 잡이 실패해도 이미 만든 곡은 보여 준다(Codex 검수 2026-10-13 P1-4) — 종전엔 두 번째 곡이
  //    실패하면 첫 곡의 재생·선택 버튼까지 사라졌다.
  const successful = state === "succeeded" || (!generating && tracks.length > 0);
  const selectedTrackId = polling.progress?.track_id ?? tracks.at(-1)?.id;
  const sortedTracks = useMemo(() => {
    if (!selectedTrackId) return tracks;
    return [...tracks].sort((a, b) => Number(b.id === selectedTrackId) - Number(a.id === selectedTrackId));
  }, [selectedTrackId, tracks]);
  const { codes: songCodes } = useSongCodes(workId, tracks.map((track) => track.id).join(","));

  useEffect(() => {
    // 🚨 이미 만든 곡이 있으면 수업이 끝나도 결과 화면을 둔다 — 고를 곡이 있다(Codex 재검수 P1).
    if (sessionEnded && tracks.length === 0 && (failed || !jobId)) {
      window.dispatchEvent(new Event("mvs:session-ended"));
    }
  }, [failed, jobId, sessionEnded, tracks.length]);

  if (query.loading) return <LoadingPage label="노래 상태를 확인하는 중..." />;
  if (!query.data || query.error) {
    return <ErrorPage message={query.error?.message ?? "노래 상태를 불러오지 못했습니다."} />;
  }

  return (
    <StudioShell
      currentStep={4}
      remainingSlots={query.data.remaining_slots}
      notice={polling.error ? "연결이 불안정해요. 자동으로 다시 확인하고 있어요." : undefined}
    >
      <div className="result-page-content">
        {generating ? (
          <section className="generation-banner" aria-live="polite">
            <div className="wave-bars compact" aria-hidden="true">
              {[8, 18, 24, 12, 26, 16, 10, 22].map((height, index) => (
                <i key={`${height}-${index}`} style={{ "--wave-height": `${height}px` } as React.CSSProperties} />
              ))}
            </div>
            <div className="generation-copy">
              <h1 tabIndex={-1}>{cancelRequested ? "중지 요청을 확인하고 있어요" : "노래 만드는 중..."}</h1>
              <p>{cancelRequested ? "이미 완성된 응답이 있다면 노래가 나올 수 있어요" : "중지하면 지금까지 상태로 돌아와요"}</p>
            </div>
            <div className="progress-track" aria-label="노래 생성 진행 중"><span /></div>
            <button className="button-secondary" type="button" onClick={() => void stop()} disabled={cancelling || cancelRequested}>
              {cancelling ? "중지 요청 중..." : cancelRequested ? "중지 요청됨" : "중지"}
            </button>
            {/* 🎨 곡과 나란히 그리는 그림 — 보통 곡보다 먼저 나와 기다리는 동안 볼 것이 된다(2026-10-14). */}
            {!cancelRequested ? <SongCover workId={workId} jobId={jobId} generating /> : null}
          </section>
        ) : null}

        {failed ? (
          <section className="result-state-card error-state">
            <span className="state-symbol" aria-hidden="true">!</span>
            <h1 tabIndex={-1}>만들지 못했어요</h1>
            <p>
              {hummingRejected
                ? "녹음한 소리로 노래를 만들지 못했어요. 6초 이상, 멜로디가 잘 들리게 다시 녹음해 주세요"
                : failureCode ? failureMessages[failureCode] : failureMessages.INTERNAL}
            </p>
            {hummingRejected ? (
              <button className="button-primary" type="button" onClick={() => void recordAgain()}>다시 녹음하기</button>
            ) : failureCode !== "BUDGET_EXHAUSTED" ? (
              <button className="button-primary" type="button" onClick={() => void editAgain()}>다시 시도</button>
            ) : null}
          </section>
        ) : null}

        {successful ? (
          <>
            {cancelRequested && state === "succeeded" ? <div className="late-success-banner">취소 전에 완성됐어요</div> : null}
            <div className="result-heading">
              {failed ? (
                <h2>전에 만든 노래는 그대로 있어요</h2>
              ) : (
                <h1 tabIndex={-1}>{tracks.length > 1 ? "두 곡을 비교해 보세요" : "노래가 나왔어요 — 들어보세요"}</h1>
              )}
              <p>마음에 들면 완성, 아쉬우면 소개 문장이나 스타일을 고쳐서 다시 만들어요</p>
            </div>
            <div className={`track-grid${tracks.length === 1 ? " one-track" : ""}`}>
              {sortedTracks.map((track) => (
                <TrackCard
                  track={track}
                  songCode={songCodes.get(track.id)}
                  selected={track.id === selectedTrackId}
                  key={track.id}
                  cover={<SongCover workId={workId} jobId={track.job_id} title={track.title} />}
                  actions={(
                    <>
                      <button
                        className="button-primary full-width"
                        type="button"
                        disabled={finishingTrack !== null}
                        onClick={() => void finish(track)}
                      >
                        {finishingTrack === track.id ? "완성하는 중..." : "이 노래로 완성"}
                      </button>
                      <TitleEditor label="노래 제목" value={track.title} onSave={(title) => saveTrackTitle(track.id, title)} />
                    </>
                  )}
                />
              ))}
              {tracks.length === 1 ? (
                <article className="retry-card">
                  <h2>아쉬운가요?</h2>
                  <p>소개 문장이나 스타일을 바꿔서 다시 만들어 보세요. 지금 노래는 지워지지 않고 남아 있어요.</p>
                  <button
                    className="button-secondary full-width"
                    type="button"
                    onClick={() => void editAgain()}
                    disabled={query.data.remaining_slots <= 0}
                  >
                    고쳐서 다시 만들기
                  </button>
                  <small>{query.data.remaining_slots > 0 ? `남은 생성 ${query.data.remaining_slots}회를 써요` : "남은 생성이 없어요 · 선생님께 요청"}</small>
                </article>
              ) : null}
            </div>
            {tracks.length >= 2 || query.data.remaining_slots <= 0 ? (
              <p className="limit-guidance centered">{query.data.remaining_slots <= 0 ? "남은 생성이 없어요. 더 만들려면 선생님께 요청해 주세요." : "두 곡 중 마음에 드는 노래를 골라 주세요."}</p>
            ) : null}
          </>
        ) : null}

        {!jobId && tracks.length === 0 ? (
          <section className="result-state-card error-state">
            <h1 tabIndex={-1}>진행 중인 노래가 없어요</h1>
            <button className="button-primary" type="button" onClick={() => void editAgain()}>스타일로 돌아가기</button>
          </section>
        ) : null}
        {/* 🔑 「이전」은 생성 횟수와 무관하게 늘 눌린다(2026-10-07 jery 수업 중 지시).
            예전엔 돌아가는 길이 「고쳐서 다시 만들기」 하나였고, 두 곡이 나오거나 횟수를 다 쓰면 그마저 사라져 학생이 갇혔다. */}
        {!generating && (successful || (failed && failureCode === "BUDGET_EXHAUSTED")) ? (
          <div className="step-back-row">
            <button className="button-ghost" type="button" onClick={() => void editAgain()}>← 이전</button>
          </div>
        ) : null}
        {polling.stopped && polling.error ? <InlineError>진행 확인을 멈췄어요. 페이지를 새로 열어 주세요.</InlineError> : null}
        {error ? <InlineError>{error}</InlineError> : null}
      </div>
    </StudioShell>
  );
}
